// ============================================================================
// PostingSummaryHeader — header sommaire de la fiche affichage (E1d, F-306)
// ============================================================================
// FRERE de TeamSummaryHeader / CommitteeSummaryHeader (gabarit nav fiche, standard
// permanent UI_STANDARDS §Pattern fiche) : tuile + titre + chips (equipe / statut) +
// bande de metriques gap-px (publie le, echeance, piece jointe). Tokens @gs/ui
// uniquement (regle #28) — zero valeur CSS en dur.

import { ClipboardList } from 'lucide-react';
import { useTranslation } from '../../i18n';
import type { PostingDetail } from '../../hooks/usePostings';
import { PostingStatusBadge } from './PostingStatusBadge';
import { formatDate, formatDateOnly, isDeadlineSoon } from './format';

export function PostingSummaryHeader({ posting }: { posting: PostingDetail }) {
  const { t, i18n } = useTranslation();
  const soon = isDeadlineSoon(posting.deadline);

  const metrics = [
    { label: t('gest:affichages.metrics.postedAt'), value: formatDate(i18n.language, posting.postedAt) },
    {
      label: t('gest:affichages.metrics.deadline'),
      value: posting.deadline ? formatDateOnly(i18n.language, posting.deadline) : '—',
      soon,
    },
    {
      label: t('gest:affichages.metrics.attachment'),
      value: posting.attachment ? t('gest:affichages.attachment.present') : t('gest:affichages.attachment.none'),
    },
  ];

  return (
    <div className="mb-4 overflow-hidden rounded-xl border border-slate-200 bg-white shadow-sm dark:border-slate-700 dark:bg-slate-800">
      <div className="flex flex-wrap items-start gap-3 px-3.5 py-3">
        <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-brand-50 text-brand-600 dark:bg-brand-950 dark:text-brand-400">
          <ClipboardList size={20} aria-hidden="true" />
        </div>
        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-center gap-2">
            <h2 className="truncate text-head font-bold text-slate-900 dark:text-slate-50">{posting.title}</h2>
            <PostingStatusBadge status={posting.status} />
          </div>
          <p className="mt-0.5 truncate text-meta text-slate-500 dark:text-slate-400">
            {posting.teamName ?? t('gest:affichages.noTeam')}
          </p>
        </div>
      </div>
      <div className="grid grid-cols-3 gap-px border-t border-slate-200 bg-slate-200 dark:border-slate-700 dark:bg-slate-700">
        {metrics.map((m) => (
          <div key={m.label} className="bg-white px-3.5 py-2.5 dark:bg-slate-800">
            <div className="text-chip font-semibold uppercase tracking-wide text-slate-400">{m.label}</div>
            <div className={`mt-0.5 truncate text-field font-semibold tabular-nums ${
              m.soon ? 'text-amber-700 dark:text-amber-300' : 'text-slate-800 dark:text-slate-100'
            }`}>
              {m.value}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
